export type UserRank = 'Ronin' | 'Samurai' | 'General' | 'Shogun';

export interface User {
  address: string;
  username: string;
  usernameLower: string;
  createdAt: string;
  lastLogin: string;
  totalPoints: number;
  rank: UserRank;
  stakedAmount: number;
}

export const RANK_THRESHOLDS = {
  RONIN: { min: 0, max: 500 },
  SAMURAI: { min: 500, max: 2000 },
  GENERAL: { min: 2000, max: 5000 },
  SHOGUN: { min: 5000, max: Infinity }
} as const;

export const STAKE_MULTIPLIERS = {
  TIER_1: { min: 10000, max: 21000, multiplier: 1.3 },
  TIER_2: { min: 21000, max: 50000, multiplier: 1.5 },
  TIER_3: { min: 50000, max: 100000, multiplier: 1.7 },
  TIER_4: { min: 100000, max: 200000, multiplier: 2.0 },
  TIER_5: { min: 200000, max: Infinity, multiplier: 2.2 }
} as const;

export const RANK_COLORS = {
  Ronin: {
    text: 'text-amber-600', // Bronze
    bg: 'bg-amber-600',
    border: 'border-amber-600',
    gradient: 'from-amber-700 to-amber-500'
  },
  Samurai: {
    text: 'text-gray-300', // Silver
    bg: 'bg-gray-300',
    border: 'border-gray-300',
    gradient: 'from-gray-400 to-gray-200'
  },
  General: {
    text: 'text-yellow-500', // Gold
    bg: 'bg-yellow-500',
    border: 'border-yellow-500',
    gradient: 'from-yellow-600 to-yellow-400'
  },
  Shogun: {
    text: 'text-cyan-200', // Platinum
    bg: 'bg-cyan-200',
    border: 'border-cyan-200',
    gradient: 'from-cyan-300 to-cyan-100'
  }
} as const;

export function calculateRank(points: number): UserRank {
  if (points >= RANK_THRESHOLDS.SHOGUN.min) return 'Shogun';
  if (points >= RANK_THRESHOLDS.GENERAL.min) return 'General';
  if (points >= RANK_THRESHOLDS.SAMURAI.min) return 'Samurai';
  return 'Ronin';
}

export function calculatePointMultiplier(stakedAmount: number): number {
  if (stakedAmount >= STAKE_MULTIPLIERS.TIER_5.min) return STAKE_MULTIPLIERS.TIER_5.multiplier;
  if (stakedAmount >= STAKE_MULTIPLIERS.TIER_4.min) return STAKE_MULTIPLIERS.TIER_4.multiplier;
  if (stakedAmount >= STAKE_MULTIPLIERS.TIER_3.min) return STAKE_MULTIPLIERS.TIER_3.multiplier;
  if (stakedAmount >= STAKE_MULTIPLIERS.TIER_2.min) return STAKE_MULTIPLIERS.TIER_2.multiplier;
  if (stakedAmount >= STAKE_MULTIPLIERS.TIER_1.min) return STAKE_MULTIPLIERS.TIER_1.multiplier;
  return 1.0;
}

export function calculateMultipliedPoints(basePoints: number, stakedAmount: number): number {
  const multiplier = calculatePointMultiplier(stakedAmount);
  return Math.floor(basePoints * multiplier);
}